import React, { useState, useEffect } from 'react';
import { authFetch, authHeaders } from '../utils/authFetch';
import './RingPairingModal.css';

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_ORIGIN || 'https://app.neurolonic.com/api';

// ESP32-C3 field recorder BLE UUIDs (must match firmware exactly)
const SERVICE_UUID      = '12345678-1234-1234-1234-123456789abc';
const CHAR_UUID_AUDIO   = '12345678-1234-1234-1234-123456789abd'; // NOTIFY
const CHAR_UUID_COMMAND = '12345678-1234-1234-1234-123456789abe'; // WRITE
const DEVICE_NAME       = 'ESP32C3_AudioRec';

// ESP32 USB Serial identifiers (CP2102 / CH340 / native USB)
const ESP32_USB_FILTERS = [
  { usbVendorId: 0x10C4 },  // Silicon Labs CP210x
  { usbVendorId: 0x1A86 },  // QinHeng CH340
  { usbVendorId: 0x303A },  // Espressif native USB
  { usbVendorId: 0x0403 },  // FTDI
];

function RingPairingModal({ open, onClose, userId, onPaired, onUnpaired, onSerialConnected, onBLEConnected }) {
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState(null);
  const [ringId, setRingId] = useState(null);
  const [connectionType, setConnectionType] = useState(null); // 'serial' | 'ble'

  // Check pairing status on mount
  useEffect(() => {
    if (open && userId) {
      checkPairingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const checkPairingStatus = async () => {
    try {
      const response = await authFetch(`${BACKEND_ORIGIN}/ring/status?user_id=${userId}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.status_code === 200 && data.data.paired) {
        setRingId(data.data.ring_id);
      }
    } catch (err) {
      console.error('Failed to check pairing status:', err);
    }
  };

  // =============================================
  // USB SERIAL CONNECTION (Primary method)
  // =============================================
  const connectViaSerial = async () => {
    try {
      setPairing(true);
      setError(null);

      if (!navigator.serial) {
        throw new Error('Web Serial API is not supported. Please use Chrome 89+ or Edge 89+.');
      }

      console.log('[Serial] Requesting serial port...');

      // Show the serial port picker — try ESP32 filters first, fall back to all ports
      let port;
      try {
        port = await navigator.serial.requestPort({ filters: ESP32_USB_FILTERS });
      } catch (e) {
        if (e.name === 'NotFoundError') {
          // User cancelled or no matching device — try without filters
          console.log('[Serial] No filtered device found, showing all ports...');
          port = await navigator.serial.requestPort();
        } else {
          throw e;
        }
      }

      const portInfo = port.getInfo();
      console.log('[Serial] Port selected:', portInfo);

      const serialRingId = `serial-${portInfo.usbVendorId || 'unknown'}-${portInfo.usbProductId || 'unknown'}`;
      console.log('[Serial] Ring ID:', serialRingId);

      // Pair with backend
      console.log('[Serial] Sending pair request to backend...');
      const response = await authFetch(`${BACKEND_ORIGIN}/ring/pair`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          ring_id: serialRingId
        })
      });

      const result = await response.json();
      console.log('[Serial] Backend pair response:', result);

      if (result.status_code === 200) {
        setRingId(serialRingId);
        setConnectionType('serial');
        if (onPaired) onPaired(serialRingId);
        if (onSerialConnected) onSerialConnected(port);
        console.log('[Serial] Pairing complete! Port ready for audio streaming.');
        alert('Pi Ring connected via USB! Audio will auto-stream to dashboard.');
      } else {
        throw new Error(result.message || 'Failed to pair ring');
      }

    } catch (err) {
      console.error('[Serial] Connection failed:', err.name, err.message);
      if (err.name === 'NotFoundError') {
        setError('No serial port selected. Please plug in the Pi Ring via USB and try again.');
      } else {
        setError(err.message);
      }
    } finally {
      setPairing(false);
    }
  };

  // =============================================
  // BLE CONNECTION
  // =============================================
  const connectViaBLE = async () => {
    try {
      setPairing(true);
      setError(null);

      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth is not supported. Please use Chrome or Edge.');
      }

      const available = await navigator.bluetooth.getAvailability();
      if (!available) {
        throw new Error('Bluetooth is not available. Make sure Bluetooth is turned on.');
      }

      console.log('[BLE] Requesting device...');

      // Try exact name first, fall back to all devices
      let device = null;
      try {
        device = await navigator.bluetooth.requestDevice({
          filters: [{ name: DEVICE_NAME }],
          optionalServices: [SERVICE_UUID]
        });
      } catch (e1) {
        console.warn('[BLE] Name filter failed, showing all devices...');
        device = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: [SERVICE_UUID]
        });
      }

      console.log('[BLE] Device selected:', device.name || device.id);

      // Connect and verify the service is present
      const server  = await device.gatt.connect();
      await server.getPrimaryService(SERVICE_UUID);   // throws if wrong device
      await server.getPrimaryService(SERVICE_UUID).then(svc => Promise.all([
        svc.getCharacteristic(CHAR_UUID_AUDIO),
        svc.getCharacteristic(CHAR_UUID_COMMAND),
      ]));
      console.log('[BLE] GATT connected and service verified');

      const extractedRingId = device.name || device.id;
      setConnectionType('ble');

      // Register with backend (best-effort — don't block on failure)
      try {
        const response = await authFetch(`${BACKEND_ORIGIN}/ring/pair`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, ring_id: extractedRingId })
        });
        const result = await response.json();
        if (result.status_code !== 200) {
          console.warn('[BLE] Backend pair returned non-200:', result);
        }
      } catch (pairErr) {
        console.warn('[BLE] Backend pair request failed (non-fatal):', pairErr.message);
      }

      setRingId(extractedRingId);
      // Pass the device object so RingAudioHandler can manage the full connection
      if (onBLEConnected) onBLEConnected(device);
      if (onPaired) onPaired(extractedRingId);

    } catch (err) {
      console.error('[BLE] Pairing failed:', err);
      setError(err.message);
    } finally {
      setPairing(false);
    }
  };

  // =============================================
  // UNPAIR
  // =============================================
  const unpairRing = async () => {
    if (!window.confirm('Are you sure you want to unpair your Pi Ring?')) return;

    try {
      setPairing(true);
      setError(null);

      const response = await authFetch(`${BACKEND_ORIGIN}/ring/unpair`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      const result = await response.json();

      if (result.status_code === 200) {
        setRingId(null);
        setConnectionType(null);
        if (onUnpaired) onUnpaired();
        alert('Pi Ring unpaired successfully!');
      } else {
        throw new Error(result.message || 'Failed to unpair ring');
      }

    } catch (err) {
      console.error('Unpair failed:', err);
      setError(err.message);
    } finally {
      setPairing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="ring-modal-overlay" onClick={onClose}>
      <div className="ring-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="ring-modal-close" onClick={onClose}>×</button>

        <h2 className="ring-modal-title">Pi Ring Connection</h2>

        {error && (
          <div className="ring-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {ringId ? (
          <div className="ring-paired-status">
            <div className="ring-status-icon">✓</div>
            <p><strong>Ring ID:</strong> {ringId}</p>
            {connectionType && (
              <p className="ring-connected-badge">
                Connected via {connectionType === 'serial' ? 'USB Serial' : 'Bluetooth'}
              </p>
            )}
            <p style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: 8 }}>
              Audio recordings will automatically be analyzed and saved to your dashboard.
            </p>
            <button
              className="ring-btn ring-btn-danger"
              onClick={unpairRing}
              disabled={pairing}
            >
              {pairing ? 'Unpairing...' : 'Unpair Ring'}
            </button>
          </div>
        ) : (
          <div className="ring-unpaired-status">
            <p>Connect your Pi Ring to enable automatic audio recording, emotion analysis, and dashboard auto-save.</p>

            {/* PRIMARY: USB Serial */}
            <div style={{ marginTop: 16 }}>
              <button
                className="ring-btn ring-btn-primary"
                onClick={connectViaSerial}
                disabled={pairing}
                style={{ width: '100%', padding: '12px 20px', fontSize: '1rem' }}
              >
                {pairing ? 'Connecting...' : 'Connect via USB (Recommended)'}
              </button>
              <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 6 }}>
                Plug in the Pi Ring via USB cable. Works reliably on all platforms.
              </p>
            </div>

            {/* SECONDARY: Bluetooth */}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                className="ring-btn"
                onClick={connectViaBLE}
                disabled={pairing}
                style={{ width: '100%', padding: '10px 16px', fontSize: '0.9rem', background: '#444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                {pairing ? 'Connecting...' : 'Connect via Bluetooth'}
              </button>
              <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 6 }}>
                Wireless connection. Requires BLE-compatible browser and adapter.
              </p>
            </div>
          </div>
        )}

        <div className="ring-modal-info">
          <p><small>Pi Ring auto-streams 8-second audio recordings to the prediction API and saves results to your dashboard automatically.</small></p>
        </div>
      </div>
    </div>
  );
}

export default RingPairingModal;
